import type { SimplifiedProductDraft } from '../../redux/ownerActivationSlice';
import type {
  ProductFormData,
  ProductPriceType,
} from '../products/newproducts/productFormTypes';
import {
  getServiceChargeType,
  getServicePriceType,
  getServiceUnit,
  type ProductKind,
} from '../../utils/productKind';

/**
 * Draft mapping for the wizard's simplified first-product step (US3, T055;
 * product-creation contract §9).
 *
 * Only the simplified text/numeric fields and the Type (product or service,
 * and how a service is charged) persist in activation state; image
 * base64/URIs, barcodes, variants, and other media are never persisted.
 */

/** The step's Type choice and the price type it sets on the form. */
export interface FirstProductItemType {
  kind: ProductKind;
  priceType: ProductPriceType | string;
}

const PRODUCT_ITEM_TYPE: FirstProductItemType = {
  kind: 'product',
  priceType: 'item',
};

const isServiceDraft = (draft: SimplifiedProductDraft): boolean =>
  draft.kind === 'service';

/** Seed the per-instance product form from a persisted simplified draft. */
export const draftToFormSeed = (
  draft: SimplifiedProductDraft | null | undefined,
): Partial<ProductFormData> => {
  if (!draft) return {};
  const unit = isServiceDraft(draft) ? getServiceUnit(draft.chargeType) : '';
  return {
    productname: draft.productName || '',
    category: draft.category || '',
    buyingprice: draft.buyingPrice || '',
    sellingprice: draft.sellingPrice || '',
    quantity: draft.quantity || '',
    // A per-hour service carries its hour unit, as the form sets it
    ...(unit ? { unitofmass: unit } : {}),
  };
};

/**
 * Seed the step's Type from a persisted draft. Drafts saved before the Type
 * dropdown carry no kind and reopen as a product.
 */
export const draftToItemTypeSeed = (
  draft: SimplifiedProductDraft | null | undefined,
): FirstProductItemType => {
  if (!draft || !isServiceDraft(draft)) return PRODUCT_ITEM_TYPE;
  return { kind: 'service', priceType: getServicePriceType(draft.chargeType) };
};

export type SimplifiedDraftValues = Omit<SimplifiedProductDraft, 'updatedAt'>;

/** The typed fields only: what counts as draft content. */
export type SimplifiedDraftText = Omit<
  SimplifiedDraftValues,
  'kind' | 'chargeType'
>;

/**
 * Project the live form back to the persistable simplified draft fields.
 * Only the five simplified fields and the Type are read; media/barcode/
 * variants are never persisted.
 */
export const formToDraftValues = (
  formData: Pick<
    ProductFormData,
    'productname' | 'category' | 'buyingprice' | 'sellingprice' | 'quantity'
  > &
    Partial<ProductFormData>,
  itemType: FirstProductItemType = PRODUCT_ITEM_TYPE,
): SimplifiedDraftValues => ({
  productName: formData.productname || '',
  category: formData.category || '',
  buyingPrice: formData.buyingprice || '',
  sellingPrice: formData.sellingprice || '',
  quantity: formData.quantity || '',
  kind: itemType.kind,
  chargeType:
    itemType.kind === 'service'
      ? getServiceChargeType(itemType.priceType)
      : 'per_service',
});

/**
 * True when any simplified draft field carries user input worth persisting.
 * The Type alone is not content: an untouched form never writes a draft.
 */
export const hasDraftContent = (values: SimplifiedDraftText): boolean =>
  Boolean(
    values.productName.trim() ||
      values.category.trim() ||
      values.buyingPrice.trim() ||
      values.sellingPrice.trim() ||
      values.quantity.trim(),
  );

/** True when two simplified draft projections differ (skip redundant writes). */
export const draftValuesDiffer = (
  a: SimplifiedDraftValues,
  b: SimplifiedDraftValues,
): boolean =>
  a.productName !== b.productName ||
  a.category !== b.category ||
  a.buyingPrice !== b.buyingPrice ||
  a.sellingPrice !== b.sellingPrice ||
  a.quantity !== b.quantity ||
  a.kind !== b.kind ||
  a.chargeType !== b.chargeType;
